import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  getCountryVatRateForCategory,
  getCountryCurrencySymbol,
  getCountryVatRateProfile,
  normalizeVatDataForCountry
} from '../lib/receiptCountryUtils';

// Create the context
const ReceiptScannerContext = createContext();

/**
 * Enhanced data extraction utilities for better receipt processing
 */
const DataExtractionUtils = {
  /**
   * Extract vendor information with improved pattern matching
   */
  extractVendor: (text, lines) => {
    // Common vendor patterns for beauty industry
    const beautyVendors = [
      { pattern: /boots/i, name: 'Boots', category: 'beauty' },
      { pattern: /sally\s*beauty/i, name: 'Sally Beauty', category: 'beauty' },
      { pattern: /ulta/i, name: 'Ulta Beauty', category: 'beauty' },
      { pattern: /sephora/i, name: 'Sephora', category: 'beauty' },
      { pattern: /l'oreal/i, name: "L'Oreal", category: 'beauty' },
      { pattern: /schwarzkopf/i, name: 'Schwarzkopf', category: 'beauty' },
      { pattern: /redken/i, name: 'Redken', category: 'beauty' },
      { pattern: /matrix/i, name: 'Matrix', category: 'beauty' }
    ];

    // Check for known beauty vendors first
    for (const vendor of beautyVendors) {
      if (vendor.pattern.test(text)) {
        return { name: vendor.name, category: vendor.category };
      }
    }

    // Fall back to first meaningful line
    for (let i = 0; i < Math.min(5, lines.length); i++) {
      const line = lines[i].trim();
      if (line && line.length > 2 && !/^(receipt|invoice|bill)$/i.test(line)) {
        return { name: line, category: 'supplies' };
      }
    }

    return { name: 'Unknown Vendor', category: 'supplies' };
  },

  /**
   * Extract amount with multiple pattern attempts
   */
  extractAmount: (text) => {
    const amountPatterns = [
      /total\s*(?:amount|due)?\s*[€£$]?\s*(\d+[.,]\d{2})/i,
      /(?:grand\s+)?total\s*[€£$]?\s*(\d+[.,]\d{2})/i,
      /amount\s*(?:due|owing)?\s*[€£$]?\s*(\d+[.,]\d{2})/i,
      /balance\s*(?:due)?\s*[€£$]?\s*(\d+[.,]\d{2})/i,
      /pay\s*[€£$]?\s*(\d+[.,]\d{2})/i,
      /[€£$]\s*(\d+[.,]\d{2})\s*(?:total|due|owing)/i
    ];

    for (const pattern of amountPatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        const amount = parseFloat(match[1].replace(',', '.'));
        if (amount > 0 && amount < 10000) { // Reasonable bounds
          return amount;
        }
      }
    }

    return 0;
  },

  /**
   * Extract date with improved parsing
   */
  extractDate: (text) => {
    const datePatterns = [
      /(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/,
      /(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2})/,
      /(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})/
    ];

    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        let day, month, year;
        
        if (match[3].length === 4) {
          // DD/MM/YYYY format
          day = match[1].padStart(2, '0');
          month = match[2].padStart(2, '0');
          year = match[3];
        } else if (match[1].length === 4) {
          // YYYY/MM/DD format
          year = match[1];
          month = match[2].padStart(2, '0');
          day = match[3].padStart(2, '0');
        } else {
          // DD/MM/YY format
          day = match[1].padStart(2, '0');
          month = match[2].padStart(2, '0');
          year = `20${match[3]}`;
        }

        // Validate date
        const dateObj = new Date(`${year}-${month}-${day}`);
        if (!isNaN(dateObj.getTime())) {
          return `${year}-${month}-${day}`;
        }
      }
    }

    return new Date().toISOString().split('T')[0];
  },

  /**
   * Enhanced VAT extraction with multiple strategies
   */
  extractVATInfo: (text, totalAmount) => {
    let vatAmount = null;
    let vatRate = null;

    // Strategy 1: Look for explicit VAT amounts
    const vatAmountPatterns = [
      /vat(?:\s+amount)?(?:\s*[:]\s*)[€£$]?(\d+[.,]\d+)/i,
      /[€£$]?\s*(\d+[.,]\d+)\s*(?:vat|tax)/i,
      /vat\s*[€£$]?\s*(\d+[.,]\d+)/i
    ];

    for (const pattern of vatAmountPatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        const amount = parseFloat(match[1].replace(',', '.'));
        if (amount > 0 && amount < totalAmount) {
          vatAmount = amount;
          break;
        }
      }
    }

    // Strategy 2: Look for VAT rates
    const vatRatePatterns = [
      /(\d+(?:\.\d+)?)\s*%\s*vat/i,
      /vat\s*(?:@|at)?\s*(\d+(?:\.\d+)?)\s*%/i,
      /tax\s*(?:@|at)?\s*(\d+(?:\.\d+)?)\s*%/i
    ];

    for (const pattern of vatRatePatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        const rate = parseFloat(match[1]);
        if (rate >= 0 && rate <= 30) { // Reasonable VAT rate bounds
          vatRate = rate / 100;
          break;
        }
      }
    }

    // Strategy 3: Check for VAT exempt indicators
    const vatExemptPatterns = [
      /vat\s*exempt/i,
      /no\s*vat/i,
      /vat\s*free/i,
      /0\s*%\s*vat/i,
      /vat\s*0\s*%/i
    ];

    for (const pattern of vatExemptPatterns) {
      if (pattern.test(text)) {
        vatAmount = 0;
        vatRate = 0;
        break;
      }
    }

    return { vatAmount, vatRate };
  },

  /**
   * Extract key data from Irish utility bills (e.g., Electric Ireland),
   * including total due and explicit VAT details when present.
   */
  extractUtilityBillData: (text) => {
    if (!text) return null;

    const textLower = text.toLowerCase();
    const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);

    const looksUtility = /electric ireland|bill summary|billing period|total due|direct debit|electricity|gas|utility|energy|eir|vodafone|virgin media|sse|airtricity|energia|bord gais/i.test(textLower);
    if (!looksUtility) return null;

    const parseLineAmount = (line) => {
      const match = line.match(/-?\d+[.,]\d{2}/);
      if (!match) return null;
      const parsed = parseFloat(match[0].replace(',', '.'));
      if (!Number.isFinite(parsed)) return null;
      const isCredit = /\b(cr|credit)\b/i.test(line);
      return isCredit && parsed > 0 ? -parsed : parsed;
    };

    const findAmountForKeywords = (keywords, { forcePositive = false } = {}) => {
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lower = line.toLowerCase();
        if (!keywords.some((keyword) => lower.includes(keyword))) continue;

        let amount = parseLineAmount(line);
        if (amount === null && i + 1 < lines.length) {
          amount = parseLineAmount(lines[i + 1]);
        }
        if (amount === null) continue;
        return forcePositive ? Math.abs(amount) : amount;
      }
      return null;
    };

    const vendor = textLower.includes('electric ireland') ? 'Electric Ireland' : null;
    const totalDue = findAmountForKeywords(
      ['total due', 'total amount', 'amount due', 'amount payable', 'total to pay', 'total cash'],
      { forcePositive: true }
    );
    const charges = findAmountForKeywords(['charges for this period', 'charges for this'], { forcePositive: true });
    const savings = findAmountForKeywords(['your savings', 'savings'], { forcePositive: true });
    let vatAmount = findAmountForKeywords(['vat'], { forcePositive: true });

    let amount = totalDue;
    if (!amount && charges) {
      amount = charges;
      if (vatAmount) amount += vatAmount;
      if (savings) amount -= savings;
    }

    if (!amount) {
      amount = findAmountForKeywords(['balance due', 'amount due', 'due'], { forcePositive: true }) || 0;
    }

    let vatRate = null;
    const vatRateMatch =
      text.match(/\bvat\b[^%\n\r]{0,30}(\d+(?:[.,]\d+)?)\s*%/i) ||
      text.match(/(\d+(?:[.,]\d+)?)\s*%\s*vat/i);
    if (vatRateMatch && vatRateMatch[1]) {
      const parsedRate = parseFloat(vatRateMatch[1].replace(',', '.'));
      if (Number.isFinite(parsedRate) && parsedRate >= 0 && parsedRate <= 30) {
        vatRate = parsedRate / 100;
      }
    }

    const items = amount
      ? [
          {
            description: `${vendor || 'Utility'} Charges`,
            quantity: 1,
            unitPrice: amount,
            amount,
            category: 'utilities',
            vatRate: vatRate ?? null,
            vatAmount: vatAmount ?? null
          }
        ]
      : [];

    return {
      vendor,
      category: 'utilities',
      amount,
      vatAmount: vatAmount ?? null,
      vatRate: vatRate ?? null,
      items
    };
  },

  /**
   * Extract line items with improved parsing
   */
  extractLineItems: (text, totalAmount) => {
    const lines = text.split('\n');
    const items = [];
    
    // Pattern for line items: Description followed by price
    const itemPatterns = [
      /^(.+?)\s+[€£$]?\s*(\d+[.,]\d{2})$/,
      /^(.+?)\s+(\d+)\s+x\s+[€£$]?\s*(\d+[.,]\d{2})\s+[€£$]?\s*(\d+[.,]\d{2})$/,
      /^(\d+)\s+(.+?)\s+[€£$]?\s*(\d+[.,]\d{2})$/
    ];

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine || trimmedLine.length < 5) continue;

      // Skip header/footer lines
      if (/^(receipt|invoice|total|subtotal|vat|tax|thank\s+you)/i.test(trimmedLine)) {
        continue;
      }

      for (const pattern of itemPatterns) {
        const match = trimmedLine.match(pattern);
        if (match) {
          let description, quantity = 1, unitPrice, amount;

          if (match.length === 3) {
            // Simple description + price
            description = match[1].trim();
            amount = parseFloat(match[2].replace(',', '.'));
            unitPrice = amount;
          } else if (match.length === 5) {
            // Quantity x unit price = total
            description = match[2].trim();
            quantity = parseInt(match[1]);
            unitPrice = parseFloat(match[3].replace(',', '.'));
            amount = parseFloat(match[4].replace(',', '.'));
          } else if (match.length === 4) {
            // Item code + description + price
            description = match[2].trim();
            amount = parseFloat(match[3].replace(',', '.'));
            unitPrice = amount;
          }

          if (description && amount > 0 && amount < totalAmount) {
            items.push({
              description,
              quantity,
              unitPrice,
              amount,
              category: DataExtractionUtils.categorizeItem(description)
            });
          }
          break;
        }
      }
    }

    return items;
  },

  /**
   * Categorize items based on description
   */
  categorizeItem: (description) => {
    const desc = description.toLowerCase();
    
    if (/hair|shampoo|conditioner|styling|color|dye|bleach/.test(desc)) {
      return 'hair_products';
    }
    if (/nail|polish|manicure|pedicure/.test(desc)) {
      return 'nail_products';
    }
    if (/makeup|foundation|lipstick|mascara|eyeshadow/.test(desc)) {
      return 'makeup';
    }
    if (/skin|cream|lotion|cleanser|moisturizer/.test(desc)) {
      return 'skincare';
    }
    if (/tool|brush|comb|scissors|dryer|equipment/.test(desc)) {
      return 'tools_equipment';
    }
    if (/towel|cape|gown|disposable/.test(desc)) {
      return 'salon_supplies';
    }
    
    return 'beauty_supplies';
  },

  /**
   * Determine receipt type and category
   */
  determineReceiptType: (text, vendor) => {
    const textLower = text.toLowerCase();
    const vendorLower = vendor.toLowerCase();

    // Medical/Healthcare
    if (/medical|doctor|clinic|hospital|pharmacy|dental|gp/.test(textLower) ||
        /medical|doctor|clinic|hospital|pharmacy|dental|gp/.test(vendorLower)) {
      return { type: 'medical', category: 'medical_expenses', vatExempt: true };
    }

    // Utilities
    if (/electric|gas|water|utility|bill|energy|broadband|internet|phone/.test(textLower) ||
        /electric|gas|water|utility|energy|eir|vodafone|virgin/.test(vendorLower)) {
      return { type: 'utility', category: 'utilities', vatExempt: false };
    }

    // Vehicle fuel
    if (/fuel|diesel|petrol|gas|station|circle\s*k|applegreen|topaz/.test(textLower) ||
        /circle\s*k|applegreen|topaz|maxol/.test(vendorLower)) {
      return { type: 'fuel', category: 'vehicle_fuel', vatExempt: false };
    }

    // Beauty/Salon
    if (/beauty|salon|hair|nail|makeup|cosmetic|spa/.test(textLower) ||
        /beauty|salon|hair|nail|makeup|cosmetic|spa|boots|sephora/.test(vendorLower)) {
      return { type: 'beauty', category: 'beauty_supplies', vatExempt: false };
    }

    // Office/Supplies
    if (/office|supplies|stationery|paper|pen|printer/.test(textLower) ||
        /office|supplies|staples|officeworks/.test(vendorLower)) {
      return { type: 'office', category: 'office_supplies', vatExempt: false };
    }

    return { type: 'general', category: 'supplies', vatExempt: false };
  }
};

/**
 * Enhanced receipt processing function
 */
const processReceiptData = (ocrText, imageFile, metadata = {}) => {
  console.log('Processing receipt with enhanced extraction');
  
  if (!ocrText) {
    console.warn('No OCR text provided');
    return generateFallbackData(imageFile, metadata);
  }

  const countryInput = metadata?.countryId || metadata?.country;
  const defaultCurrencySymbol = getCountryCurrencySymbol(countryInput);

  const lines = ocrText.split('\n').map(line => line.trim()).filter(line => line);
  
  // Extract basic information
  const vendorInfo = DataExtractionUtils.extractVendor(ocrText, lines);
  const date = DataExtractionUtils.extractDate(ocrText);
  
  // Determine receipt type and category
  const receiptType = DataExtractionUtils.determineReceiptType(ocrText, vendorInfo.name);

  const utilityData = receiptType.type === 'utility'
    ? DataExtractionUtils.extractUtilityBillData(ocrText)
    : null;

  const amount = utilityData?.amount ?? DataExtractionUtils.extractAmount(ocrText);
  const resolvedVendor = utilityData?.vendor || vendorInfo.name;
  const resolvedCategory = utilityData?.category || receiptType.category;
  
  // Extract VAT information
  let vatInfo = { vatAmount: null, vatRate: null };
  if (receiptType.vatExempt) {
    vatInfo = { vatAmount: 0, vatRate: 0 };
  } else if (utilityData?.vatAmount !== null || utilityData?.vatRate !== null) {
    vatInfo = {
      vatAmount: utilityData?.vatAmount ?? null,
      vatRate: utilityData?.vatRate ?? null
    };
  } else {
    vatInfo = DataExtractionUtils.extractVATInfo(ocrText, amount);
  }

  if (vatInfo.vatRate === null && !receiptType.vatExempt) {
    const fallbackVatRate = getCountryVatRateForCategory(resolvedCategory, countryInput);
    if (Number.isFinite(fallbackVatRate) && fallbackVatRate >= 0) {
      vatInfo.vatRate = fallbackVatRate;
      if (vatInfo.vatAmount === null && Number(amount) > 0 && fallbackVatRate > 0) {
        vatInfo.vatAmount = parseFloat((amount - (amount / (1 + fallbackVatRate))).toFixed(2));
      }
    }
  }
  
  // Extract line items
  const items = utilityData?.items || DataExtractionUtils.extractLineItems(ocrText, amount);
  
  // Validate and adjust VAT calculations
  if (vatInfo.vatAmount !== null && amount > 0) {
    // Ensure VAT amount is reasonable
    if (vatInfo.vatAmount >= amount) {
      console.warn('VAT amount seems incorrect, recalculating');
      vatInfo.vatAmount = null;
    }
  }

  const extractedData = {
    date,
    amount,
    vendor: resolvedVendor,
    category: resolvedCategory,
    items,
    vatAmount: vatInfo.vatAmount,
    vatRate: vatInfo.vatRate,
    currency: defaultCurrencySymbol,
    receiptType: receiptType.type,
    notes: metadata.notes || '',
    ...metadata // Allow metadata to override
  };

  const normalizedData = normalizeVatDataForCountry(extractedData, {
    country: countryInput,
    text: ocrText
  });

  console.log('Enhanced extraction result:', normalizedData);
  return normalizedData;
};

/**
 * Generate fallback data when OCR fails
 */
const generateFallbackData = (imageFile, metadata = {}) => {
  const filename = imageFile?.name?.toLowerCase() || '';
  const countryInput = metadata?.countryId || metadata?.country;
  const vatProfile = getCountryVatRateProfile(countryInput);
  const defaultCurrencySymbol = getCountryCurrencySymbol(countryInput);
  const standardVatRate = Number.isFinite(vatProfile.standardRate) ? vatProfile.standardRate : 0;
  
  let category = 'supplies';
  let vendor = 'Unknown Vendor';
  let amount = 0;
  let vatAmount = null;
  let vatRate = null;

  // Infer from filename
  if (filename.includes('boots')) {
    vendor = 'Boots';
    category = 'beauty_supplies';
    amount = 45.99;
    vatRate = standardVatRate;
    vatAmount = parseFloat((amount - (amount / (1 + standardVatRate))).toFixed(2));
  } else if (filename.includes('salon') || filename.includes('hair')) {
    vendor = 'Hair Salon';
    category = 'beauty_services';
    amount = 85.00;
    vatRate = standardVatRate;
    vatAmount = parseFloat((amount - (amount / (1 + standardVatRate))).toFixed(2));
  } else if (filename.includes('medical') || filename.includes('doctor')) {
    vendor = 'Medical Center';
    category = 'medical_expenses';
    amount = 60.00;
    vatAmount = 0;
    vatRate = 0;
  }

  return normalizeVatDataForCountry({
    date: new Date().toISOString().split('T')[0],
    amount,
    vendor,
    category,
    items: [],
    vatAmount,
    vatRate,
    currency: defaultCurrencySymbol,
    receiptType: 'fallback',
    notes: 'Extracted from filename pattern',
    ...metadata
  }, {
    country: countryInput,
    text: filename
  });
};

/**
 * Receipt Scanner Provider Component with Enhanced Processing
 */
export function ReceiptScannerProvider({ children }) {
  // State
  const [apiKey, setApiKey] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [settings, setSettings] = useState({
    autoCategories: true,
    defaultCategory: 'supplies',
    autoSync: true,
    saveReceiptImages: true,
    useGoogleVision: true,
    enhancedExtraction: true, // New setting for enhanced extraction
    fallbackEnabled: true,
    confidenceThreshold: 0.7
  });
  
  // Load saved configuration on mount
  useEffect(() => {
    const loadSavedConfig = () => {
      try {
        const savedApiKey = localStorage.getItem('receiptScannerApiKey');
        if (savedApiKey) setApiKey(savedApiKey);
        
        const savedEnabledState = localStorage.getItem('receiptScannerEnabled');
        if (savedEnabledState) setIsEnabled(JSON.parse(savedEnabledState));
        
        const savedSettings = localStorage.getItem('receiptScannerSettings');
        if (savedSettings) {
          setSettings(prev => ({ ...prev, ...JSON.parse(savedSettings) }));
        }
      } catch (error) {
        console.error('Failed to load receipt scanner configuration:', error);
      }
    };
    
    loadSavedConfig();
  }, []);
  
  // Save configuration when it changes
  useEffect(() => {
    const saveConfig = () => {
      try {
        if (apiKey) localStorage.setItem('receiptScannerApiKey', apiKey);
        localStorage.setItem('receiptScannerEnabled', JSON.stringify(isEnabled));
        localStorage.setItem('receiptScannerSettings', JSON.stringify(settings));
      } catch (error) {
        console.error('Failed to save receipt scanner configuration:', error);
      }
    };
    
    saveConfig();
  }, [apiKey, isEnabled, settings]);
  
  /**
   * Enhanced receipt processing function
   */
  const processReceipt = async (imageFile, ocrText, metadata = {}) => {
    try {
      let extractedData;
      
      if (settings.enhancedExtraction && ocrText) {
        // Use enhanced extraction
        extractedData = processReceiptData(ocrText, imageFile, metadata);
      } else if (settings.fallbackEnabled) {
        // Use fallback extraction
        extractedData = generateFallbackData(imageFile, metadata);
      } else {
        throw new Error('No extraction method available');
      }

      // Apply auto-categorization if enabled
      if (settings.autoCategories && extractedData.category === 'supplies') {
        extractedData.category = settings.defaultCategory;
      }

      return {
        success: true,
        data: extractedData,
        confidence: extractedData.receiptType === 'fallback' ? 0.3 : 0.8
      };
    } catch (error) {
      console.error('Receipt processing failed:', error);
      
      if (settings.fallbackEnabled) {
        return {
          success: true,
          data: generateFallbackData(imageFile, metadata),
          confidence: 0.1,
          error: error.message
        };
      }
      
      throw error;
    }
  };
  
  /**
   * Update a specific setting
   */
  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  /**
   * Enable the receipt scanner
   */
  const enableScanner = (newApiKey = null) => {
    if (newApiKey) setApiKey(newApiKey);
    setIsEnabled(true);
  };
  
  /**
   * Disable the receipt scanner
   */
  const disableScanner = () => {
    setIsEnabled(false);
  };
  
  // Context value
  const value = {
    apiKey,
    isEnabled,
    settings,
    updateSetting,
    enableScanner,
    disableScanner,
    setApiKey,
    processReceipt, // New enhanced processing function
    DataExtractionUtils // Expose utilities for external use
  };
  
  return (
    <ReceiptScannerContext.Provider value={value}>
      {children}
    </ReceiptScannerContext.Provider>
  );
}

/**
 * Custom hook to use the receipt scanner context
 */
export function useReceiptScanner() {
  const context = useContext(ReceiptScannerContext);
  if (context === undefined) {
    throw new Error('useReceiptScanner must be used within a ReceiptScannerProvider');
  }
  return context;
}

export default ReceiptScannerContext;
